/**
 * =============================================================================
 * IDS CRM Web - Editing a check form's layout (Workscope forms Phase 1, step 6)
 * =============================================================================
 *
 * The form designer's changes, as plain functions from one schema to the next.
 * Questions and sections are found by their key, never by position, so a
 * change always lands on the question the builder picked. Nothing here saves.
 *
 * Kept from the old editor: keys made from the label and never changed,
 * archive (kept for history) for anything a published version carried,
 * delete only for what was never published (the designer decides that),
 * system fields locked except the completion tick's wording.
 * =============================================================================
 */

export const QUESTION_TYPES = [
  { value: 'yes_no', label: 'Yes / No', hint: 'A check that passes or fails' },
  { value: 'yes_no_na', label: 'Yes / No / N/A', hint: 'A check that may not apply' },
  { value: 'reading', label: 'Reading', hint: 'A measurement with a unit and an acceptable range' },
  { value: 'choice', label: 'Pick one', hint: 'One answer from a list' },
  { value: 'multi_choice', label: 'Pick several', hint: 'Any number of answers from a list' },
  { value: 'text', label: 'Text', hint: 'Free text for notes and descriptions' },
  { value: 'number', label: 'Number', hint: 'A plain count or amount' },
  { value: 'date', label: 'Date', hint: 'A day, e.g. last brush change' },
  { value: 'checkbox', label: 'Tick box', hint: 'A single tick' },
  { value: 'photo', label: 'Photo', hint: 'Photos as the answer, with how many are needed' },
  { value: 'file', label: 'File', hint: 'Documents such as PDFs as the answer' },
  { value: 'signature', label: 'Signature', hint: 'Engineer or customer signs on the device' },
  { value: 'instruction', label: 'Instruction', hint: 'Text to read; nothing to answer' }
];

export const TYPE_LABELS = QUESTION_TYPES.reduce((labels, type) => ({ ...labels, [type.value]: type.label }), {
  vehicle_select: 'Vehicle select',
  seat_select: 'Seat select'
});

/** Types whose answers can fail (and so can carry fail rules). */
export const FAILABLE_TYPES = new Set(['yes_no', 'yes_no_na', 'reading', 'choice', 'multi_choice']);

/** Types with no answer of their own to pass or fail, photo or note. */
export const ANSWERLESS_TYPES = new Set(['instruction', 'photo', 'file']);

export const COMPLETION_FIELD_KEY = 'check_completed';

const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

/** A key from a label: lowercase words joined by underscores, starting with a letter, unique. */
export function keyFromLabel(label, existingKeys, { maxLength = 40, fallback = 'field' } = {}) {
  let base = String(label || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '')
    .substring(0, maxLength) || fallback;
  if (!/^[a-z]/.test(base)) base = `${fallback}_${base}`;
  if (!existingKeys.has(base)) return base;
  let counter = 1;
  while (existingKeys.has(`${base}_${counter}`)) counter += 1;
  return `${base}_${counter}`;
}

/** Every question key in the form, follow-ups included. */
export function allQuestionKeys(schema) {
  const keys = new Set();
  (schema?.sections || []).forEach(section => (section.fields || []).forEach(field => {
    if (field.field_key) keys.add(field.field_key);
    if (field.follow_up?.field?.field_key) keys.add(field.follow_up.field.field_key);
  }));
  return keys;
}

export const sortedSections = (schema) => (schema?.sections || []).slice().sort(byOrder);
export const sortedFields = (section) => (section?.fields || []).slice().sort(byOrder);

/** { section, field } for a question key, or null. */
export function findQuestion(schema, fieldKey) {
  for (const section of schema?.sections || []) {
    const field = (section.fields || []).find(item => item.field_key === fieldKey);
    if (field) return { section, field };
  }
  return null;
}

const renumber = (items) => items.map((item, index) => ({ ...item, order: index + 1 }));

const mapSections = (schema, fn) => ({ ...schema, sections: (schema.sections || []).map(fn) });

const withSectionFields = (schema, sectionKey, fn) => mapSections(schema, section => (
  section.section_key === sectionKey ? { ...section, fields: renumber(fn(sortedFields(section))) } : section
));

/** A new question of a type, with that type's starting settings. */
export function newQuestion(type, label, existingKeys) {
  const field = {
    field_key: keyFromLabel(label, existingKeys),
    label,
    field_type: type,
    required_on_save: false,
    // As the old editor: a new question is not required until the builder says so.
    required_on_complete: false,
    system_field: false
  };
  if (type === 'instruction') {
    field.body = '';
  }
  if (type === 'yes_no' || type === 'yes_no_na') {
    field.pass_value = 'true';
    field.fail_value = 'false';
  }
  if (type === 'reading') {
    field.unit = '';
    field.min = null;
    field.max = null;
  }
  if (type === 'choice' || type === 'multi_choice') {
    field.options = [];
  }
  if (type === 'photo' || type === 'file') {
    field.min_count = 1;
  }
  return field;
}

export function addSection(schema, title = 'New Section') {
  const keys = new Set((schema.sections || []).map(section => section.section_key));
  const section = {
    section_key: keyFromLabel(title, keys, { maxLength: 30, fallback: 'section' }),
    title,
    order: (schema.sections || []).length + 1,
    fields: [],
    archived: false
  };
  return { schema: { ...schema, sections: [...renumber(sortedSections(schema)), section] }, sectionKey: section.section_key };
}

/** Adds a question to a section, after a given question or at the end. */
export function addQuestion(schema, sectionKey, field, afterKey = null) {
  return withSectionFields(schema, sectionKey, fields => {
    const index = afterKey ? fields.findIndex(item => item.field_key === afterKey) : -1;
    if (index < 0) return [...fields, field];
    return [...fields.slice(0, index + 1), field, ...fields.slice(index + 1)];
  });
}

export function updateQuestion(schema, fieldKey, patch) {
  return mapSections(schema, section => ({
    ...section,
    fields: (section.fields || []).map(field => (field.field_key === fieldKey ? { ...field, ...patch } : field))
  }));
}

/** Updates (or, with null, removes) a setting on a question. */
export function setQuestionSetting(schema, fieldKey, name, value) {
  return mapSections(schema, section => ({
    ...section,
    fields: (section.fields || []).map(field => {
      if (field.field_key !== fieldKey) return field;
      const next = { ...field };
      if (value === null || value === undefined) delete next[name];
      else next[name] = value;
      return next;
    })
  }));
}

export function updateSection(schema, sectionKey, patch) {
  return mapSections(schema, section => (section.section_key === sectionKey ? { ...section, ...patch } : section));
}

export function updateFollowUpField(schema, parentKey, patch) {
  return mapSections(schema, section => ({
    ...section,
    fields: (section.fields || []).map(field => (field.field_key === parentKey && field.follow_up?.field
      ? { ...field, follow_up: { ...field.follow_up, field: { ...field.follow_up.field, ...patch } } }
      : field))
  }));
}

export function setFollowUpCondition(schema, parentKey, condition) {
  return mapSections(schema, section => ({
    ...section,
    fields: (section.fields || []).map(field => (field.field_key === parentKey && field.follow_up
      ? { ...field, follow_up: { ...field.follow_up, condition } }
      : field))
  }));
}

export function removeFollowUp(schema, parentKey) {
  return mapSections(schema, section => ({
    ...section,
    fields: (section.fields || []).map(field => {
      if (field.field_key !== parentKey) return field;
      const next = { ...field };
      delete next.follow_up;
      return next;
    })
  }));
}

/** Moves a question up (-1) or down (+1) within its section. */
export function moveQuestion(schema, fieldKey, direction) {
  const found = findQuestion(schema, fieldKey);
  if (!found) return schema;
  return withSectionFields(schema, found.section.section_key, fields => {
    const index = fields.findIndex(field => field.field_key === fieldKey);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= fields.length) return fields;
    const next = fields.slice();
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
}

/** Moves a question to the end of another section. Its key goes with it. */
export function moveQuestionToSection(schema, fieldKey, sectionKey) {
  const found = findQuestion(schema, fieldKey);
  if (!found || found.section.section_key === sectionKey) return schema;
  const without = withSectionFields(schema, found.section.section_key, fields => fields.filter(field => field.field_key !== fieldKey));
  return withSectionFields(without, sectionKey, fields => [...fields, found.field]);
}

export function moveSection(schema, sectionKey, direction) {
  const sections = sortedSections(schema);
  const index = sections.findIndex(section => section.section_key === sectionKey);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= sections.length) return schema;
  const next = sections.slice();
  [next[index], next[target]] = [next[target], next[index]];
  return { ...schema, sections: renumber(next) };
}

export const isSystemQuestion = (field) => Boolean(field?.system_field);
export const isCompletionTick = (field) => isSystemQuestion(field) && field.field_key === COMPLETION_FIELD_KEY;

export function toggleArchiveQuestion(schema, fieldKey) {
  const found = findQuestion(schema, fieldKey);
  if (!found || isSystemQuestion(found.field)) return schema;
  return updateQuestion(schema, fieldKey, { archived: !found.field.archived });
}

export const sectionHasSystemFields = (section) => (section?.fields || []).some(isSystemQuestion);

export function toggleArchiveSection(schema, sectionKey) {
  const section = (schema.sections || []).find(item => item.section_key === sectionKey);
  if (!section || (!section.archived && sectionHasSystemFields(section))) return schema;
  return updateSection(schema, sectionKey, { archived: !section.archived });
}

export function deleteQuestion(schema, fieldKey) {
  const found = findQuestion(schema, fieldKey);
  if (!found) return schema;
  return withSectionFields(schema, found.section.section_key, fields => fields.filter(field => field.field_key !== fieldKey));
}

export function deleteSection(schema, sectionKey) {
  return { ...schema, sections: renumber(sortedSections(schema).filter(section => section.section_key !== sectionKey)) };
}

/** A copy of a question straight after it: a new key, a new question (no identity, no history). */
export function duplicateQuestion(schema, fieldKey) {
  const found = findQuestion(schema, fieldKey);
  if (!found || isSystemQuestion(found.field)) return { schema, fieldKey: null };
  const label = `${found.field.label} Copy`;
  const copy = { ...found.field, field_key: keyFromLabel(label, allQuestionKeys(schema)), label, archived: false };
  delete copy.question_id;
  delete copy.follow_up;
  return { schema: addQuestion(schema, found.section.section_key, copy, fieldKey), fieldKey: copy.field_key };
}

/** The live questions before this one, in form order: what a show-when can depend on. */
export function questionsBefore(schema, fieldKey) {
  const list = [];
  for (const section of sortedSections(schema)) {
    if (section.archived) continue;
    for (const field of sortedFields(section)) {
      if (field.field_key === fieldKey) return list;
      if (!field.archived && field.field_type !== 'instruction') list.push(field);
    }
  }
  return list;
}

/** Questions whose show-when depends on this one. */
export function dependentsOf(schema, fieldKey) {
  const list = [];
  (schema?.sections || []).forEach(section => (section.fields || []).forEach(field => {
    if (field.show_when?.field_key === fieldKey) list.push(field);
  }));
  return list;
}

/** An option key from its label, unique within the question. */
export function optionValueFromLabel(label, options) {
  return keyFromLabel(label, new Set((options || []).map(option => option.value)), { maxLength: 30, fallback: 'option' });
}
